import { useEmployeStore } from "../../store";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getDefaultValueForSelect, validatePhoneNumber, validateYupStringWithSpecialCharacters } from "helpers/functionsHelpers";
import { useTranslation } from "react-i18next";
import { RequestEmploye } from "interfaces";
import { createNewEmploye, FetchBranch, GetRoleSelected, GetUserTypes, UpdateDepartament, updateEmploye } from "services";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { useEmployeList } from "../useEmployeList";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { OptionSelect } from "Common/Components";

export const useModalEmploye = () => {
  const {
    isEdit,
    isOpenModal,
    employe,
    clearAll,
    setIsOpenModal,
  } = useEmployeStore();
  const { refetchDepartament } = useEmployeList();
  const { t } = useTranslation();

  const [valueBranch, setValueBranch] = useState<OptionSelect>();
  const [valueUsertype, setValueUsertype] = useState<OptionSelect>();
  const [valueRole, setValueRole] = useState<OptionSelect>();

  const initialValues: RequestEmploye = {
    names: employe?.names || "",
    surnames: employe?.surnames || "",
    phonenumber: employe?.phonenumber || "",
    address: employe?.address || "",
    branch_id: employe?.branch?.id || 0,
    user_type: employe?.user?.userType?.id || 0,
    role_id: employe?.user?.role?.id || 0,
    email: employe?.user?.email || "",
  };

  const validationSchema = Yup.object({
    names: validateYupStringWithSpecialCharacters(t, false),
    surnames: validateYupStringWithSpecialCharacters(t, false),
    phonenumber: validatePhoneNumber(t)
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (employe?.id && isEdit) {
          await updateEmployes(employe.id, values);
        } else {
          await createEmploye(values);
        }
      } catch (error) {
        throw error;
      } finally {
        formik.resetForm();
        setSubmitting(false);
      }
    },
  });

  const createEmploye = async (values: RequestEmploye) => {
    try {
      const response = await createNewEmploye(values);

      if (response.code === "200") {
        setIsOpenModal(false);
        refetchDepartament();
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.successAdd")
        );
        clearAll();
      } else {
        clearAll();
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error")  + " " + response.message
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const updateEmployes = async (id: number, values: RequestEmploye) => {
    try {
      const response = await updateEmploye(id, values);

      if (response.code === "200") {
        setIsOpenModal(false);
        refetchDepartament();
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.success")
        );
        clearAll();
      } else {
        clearAll();
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      throw error;
    }
  };




  const { data: branchData } = useQuery({
    queryKey: ["branch"],
    queryFn: async () => {
      const response = await FetchBranch();
      return response.data.map(
        (dept): OptionSelect => ({
          label: dept.description,
          value: dept.id.toString(),
        })
      );
    },
    staleTime: 5000,
  });

  useEffect(() => {
    if (!formik.values.branch_id) {
      setValueBranch(undefined);
    } else {
      const defaultValueFind = getDefaultValueForSelect(
        branchData || [],
        formik.values.branch_id.toString()
      );
      setValueBranch(defaultValueFind);
    }
  }, [formik.values.branch_id, branchData]);


  const { data: userTypeData } = useQuery({
    queryKey: ["usertype"],
    queryFn: async () => {
      const response = await GetUserTypes();
      return response.data.map(
        (dept): OptionSelect => ({
          label: dept.description,
          value: dept.id.toString(),
        })
      );
    },
    staleTime: 5000,
  });

  useEffect(() => {
    if (!formik.values.user_type) {
      setValueUsertype(undefined);
    } else {
      const defaultValueFind = getDefaultValueForSelect(
        userTypeData || [],
        formik.values.user_type.toString()
      );
      setValueUsertype(defaultValueFind);
    }
  }, [formik.values.user_type, userTypeData]);

  const { data: roleData } = useQuery({
    queryKey: ["role"],
    queryFn: async () => {
      const response = await GetRoleSelected();
      return response.data.map(
        (rol): OptionSelect => ({
          label: rol.description,
          value: rol.id.toString(),
        })
      );
    },
    staleTime: 5000,
  });



  useEffect(() => {
    if (!formik.values.role_id) {
      setValueRole(undefined);
    } else {
      const defaultValueFind = getDefaultValueForSelect(
        roleData || [],
        formik.values.role_id.toString()
      );
      setValueRole(defaultValueFind);
    }
  }, [formik.values.role_id, roleData]);

  return {
    isEdit,
    branch: branchData,
    usertype: userTypeData,
    role: roleData,
    isOpenModal,
    formik,
    valueUsertype,
    valueBranch,
    valueRole,
    clearAll,
    setIsOpenModal,
    t,
  };
};
